import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Science as TestIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { apiService } from '../services/api';

interface SlackConfig {
  id?: string;
  webhook_url?: string;
  enabled: boolean;
  notify_new_customers: boolean;
  notify_document_uploads: boolean;
  notify_ai_analysis: boolean;
  notify_user_invitations: boolean;
  notify_system_alerts: boolean;
  notify_daily_summary: boolean;
  default_channel?: string;
  alert_channel?: string;
  rate_limit_minutes: number;
  summary_time: string;
  webhook_configured: boolean;
}

interface SlackStats {
  total_sent: number;
  success_rate: number;
  failed_count: number;
  last_sent?: string;
  notifications_by_type: Record<string, number>;
  recent_failures: any[];
}

interface SlackLog {
  id: string;
  notification_type: string;
  message_title: string;
  success: boolean;
  error_message?: string;
  sent_at: string;
  retry_count: number;
}

const SlackConfigPage: React.FC = () => {
  const { session } = useAuth();
  const [config, setConfig] = useState<SlackConfig>({
    enabled: false,
    notify_new_customers: true,
    notify_document_uploads: true,
    notify_ai_analysis: true,
    notify_user_invitations: true,
    notify_system_alerts: true,
    notify_daily_summary: false,
    rate_limit_minutes: 1,
    summary_time: '09:00',
    webhook_configured: false
  });
  
  const [stats, setStats] = useState<SlackStats | null>(null);
  const [logs, setLogs] = useState<SlackLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{success: boolean, message: string} | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadSlackConfig();
    loadSlackStats();
  }, []);

  const loadSlackConfig = async () => {
    try {
      const data = await apiService.getSlackConfig() as SlackConfig;
      setConfig(data);
    } catch (err) {
      setError('Slack設定の読み込み中にエラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const loadSlackStats = async () => {
    try {
      // TODO: Implement slack stats endpoint
      // const data = await apiService.getSlackStats() as SlackStats;
      // setStats(data);
      console.log('Slack stats not implemented yet');
    } catch (err) {
      console.error('Failed to load Slack stats:', err);
    }
  };

  const loadSlackLogs = async () => {
    try {
      // TODO: Implement slack logs endpoint
      // const data = await apiService.getSlackLogs(20) as SlackLog[];
      // setLogs(data);
      // setShowLogs(true);
      console.log('Slack logs not implemented yet');
    } catch (err) {
      setError('ログの読み込みに失敗しました');
    }
  };

  const saveSlackConfig = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Extract only the fields needed for creation
      const configData = {
        webhook_url: config.webhook_url,
        enabled: config.enabled,
        notify_new_customers: config.notify_new_customers,
        notify_document_uploads: config.notify_document_uploads,
        notify_ai_analysis: config.notify_ai_analysis,
        notify_user_invitations: config.notify_user_invitations,
        notify_system_alerts: config.notify_system_alerts,
        notify_daily_summary: config.notify_daily_summary,
        default_channel: config.default_channel,
        alert_channel: config.alert_channel,
        rate_limit_minutes: config.rate_limit_minutes,
        summary_time: config.summary_time
      };
      
      const data = await apiService.updateSlackConfig(configData) as SlackConfig;
      setConfig(data);
      setSuccess('Slack設定を保存しました');
      await loadSlackStats();
    } catch (err: any) {
      setError(err.message || 'Slack設定の保存中にエラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  const testSlackWebhook = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const data = await apiService.testSlackWebhook() as { success: boolean; message: string; };
      setTestResult(data);
      
      if (data.success) {
        await loadSlackStats();
      }
    } catch (err) {
      setTestResult({
        success: false,
        message: 'テストリクエストに失敗しました'
      });
    } finally {
      setTesting(false);
    }
  };

  const getStatusColor = (success: boolean) => {
    return success ? 'success' : 'error';
  };

  const getStatusIcon = (success: boolean) => {
    return success ? <CheckIcon /> : <ErrorIcon />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          <NotificationsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Slack通知設定
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Slackワークスペースとの連携を設定し、システムイベントの通知を管理します。
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 基本設定 */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              基本設定
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enabled}
                    onChange={(e) => setConfig({...config, enabled: e.target.checked})}
                  />
                }
                label="Slack通知を有効にする"
              />
            </Box>

            <TextField
              fullWidth
              label="Slack Webhook URL"
              value={config.webhook_url || ''}
              onChange={(e) => setConfig({...config, webhook_url: e.target.value})}
              helperText="SlackアプリのIncoming Webhook URLを入力してください"
              sx={{ mb: 3 }}
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="デフォルトチャンネル"
                  value={config.default_channel || ''}
                  onChange={(e) => setConfig({...config, default_channel: e.target.value})}
                  placeholder="#general"
                  helperText="通知を送信するデフォルトチャンネル"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="アラート専用チャンネル"
                  value={config.alert_channel || ''}
                  onChange={(e) => setConfig({...config, alert_channel: e.target.value})}
                  placeholder="#alerts"
                  helperText="システムアラート専用チャンネル"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="レート制限（分）"
                  type="number"
                  value={config.rate_limit_minutes}
                  onChange={(e) => setConfig({...config, rate_limit_minutes: parseInt(e.target.value) || 1})}
                  inputProps={{ min: 1, max: 60 }}
                  helperText="同種通知の最小間隔"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="日次サマリー送信時刻"
                  type="time"
                  value={config.summary_time}
                  onChange={(e) => setConfig({...config, summary_time: e.target.value})}
                  helperText="HH:MM形式"
                />
              </Grid>
            </Grid>

            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              通知種別設定
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.notify_new_customers}
                      onChange={(e) => setConfig({...config, notify_new_customers: e.target.checked})}
                    />
                  }
                  label="新規顧客登録"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.notify_document_uploads}
                      onChange={(e) => setConfig({...config, notify_document_uploads: e.target.checked})}
                    />
                  }
                  label="法務チェックアップロード"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.notify_ai_analysis}
                      onChange={(e) => setConfig({...config, notify_ai_analysis: e.target.checked})}
                    />
                  }
                  label="AI分析完了"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.notify_user_invitations}
                      onChange={(e) => setConfig({...config, notify_user_invitations: e.target.checked})}
                    />
                  }
                  label="ユーザー招待"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.notify_system_alerts}
                      onChange={(e) => setConfig({...config, notify_system_alerts: e.target.checked})}
                    />
                  }
                  label="システムアラート"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={config.notify_daily_summary}
                      onChange={(e) => setConfig({...config, notify_daily_summary: e.target.checked})}
                    />
                  }
                  label="日次サマリー"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={saveSlackConfig}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : undefined}
              >
                {saving ? '保存中...' : '設定を保存'}
              </Button>
              
              <Button
                variant="outlined"
                onClick={testSlackWebhook}
                disabled={testing || !config.webhook_url}
                startIcon={testing ? <CircularProgress size={20} /> : <TestIcon />}
              >
                {testing ? 'テスト中...' : 'テスト送信'}
              </Button>
            </Box>

            {testResult && (
              <Alert 
                severity={testResult.success ? 'success' : 'error'}
                sx={{ mt: 2 }}
              >
                {testResult.message}
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* 統計情報 */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              接続状態
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Chip
                icon={config.webhook_configured ? <CheckIcon /> : <ErrorIcon />}
                label={config.webhook_configured ? '設定済み' : '未設定'}
                color={config.webhook_configured ? 'success' : 'error'}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                icon={config.enabled ? <CheckIcon /> : <WarningIcon />}
                label={config.enabled ? '有効' : '無効'}
                color={config.enabled ? 'success' : 'warning'}
                size="small"
              />
            </Box>
          </Paper>

          {stats && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                統計情報（過去30日）
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  送信総数
                </Typography>
                <Typography variant="h4">
                  {stats.total_sent}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  成功率
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={stats.success_rate}
                    sx={{ flexGrow: 1, mr: 1 }}
                    color={stats.success_rate > 90 ? 'success' : stats.success_rate > 70 ? 'warning' : 'error'}
                  />
                  <Typography variant="body2">
                    {stats.success_rate.toFixed(1)}%
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  失敗数
                </Typography>
                <Typography variant="h6" color="error">
                  {stats.failed_count}
                </Typography>
              </Box>

              {stats.last_sent && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    最終送信
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(stats.last_sent)}
                  </Typography>
                </Box>
              )}

              <Button
                variant="outlined"
                size="small"
                onClick={loadSlackLogs}
                fullWidth
                sx={{ mt: 2 }}
              >
                送信ログを表示
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* ログダイアログ */}
      <Dialog
        open={showLogs}
        onClose={() => setShowLogs(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Slack送信ログ</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>送信時刻</TableCell>
                  <TableCell>通知タイプ</TableCell>
                  <TableCell>タイトル</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>リトライ回数</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>{formatDate(log.sent_at)}</TableCell>
                    <TableCell>{log.notification_type}</TableCell>
                    <TableCell>{log.message_title}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(log.success)}
                        label={log.success ? '成功' : '失敗'}
                        color={getStatusColor(log.success)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{log.retry_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogs(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SlackConfigPage;